import { TeamMember } from "../Entities/TeamMember";
import apiClient from "./api.client.service";
class TeamMemberService {
  private teamMembers: TeamMember[] = [];
  private APIBaseUrl: string;

  constructor() {
    this.APIBaseUrl = process.env.REACT_APP_API_BASE_URL ?? "";
  }

  async loadData() {
    try {
      const response = await apiClient.get(
        `${this.APIBaseUrl}/admin/organization/members`
      );
      const data = response.data;
      this.teamMembers = data;
      return data;
    } catch (error) {
      console.error("🚀 ~ TeamMemberService ~ loadData ~ error:", error);
    }
  }

  async fetchAvailableTeamMembersForInterViews() {
    try {
      const params: Record<string, any> = {
        isActive: true,
        availableForInterviews: true,
      };

      const query = new URLSearchParams(params);
      const response = await apiClient.get(
        `${this.APIBaseUrl}/admin/organization/members?${query}`
      );
      const data = response.data;
      this.teamMembers = data;
      return data;
    } catch (error) {
      console.error("🚀 ~ TeamMemberService ~ loadData ~ error:", error);
    }
  }

  async fetchAllTeamMembers() {
    try {
      const response = await apiClient.get(
        `${this.APIBaseUrl}/admin/organization/members`
      );
      return response.data;
    } catch (error) {
      console.error(
        "🚀 ~ TeamMemberService ~ fetchAllTeamMembers ~ error:",
        error
      );
      return { members: [] };
    }
  }

  async getAllTeamMembers(page: number, limit: number) {
    try {
      const response = await apiClient.get(
        `${this.APIBaseUrl}/admin/organization/members?page=${page}&limit=${limit}`
      );
      const data = response.data;
      return data;
    } catch (error) {
      console.error(
        "🚀 ~ TeamMemberService ~ getAllTeamMembers ~ error:",
        error
      );
      return [];
    }
  }

  async getTeamMemberById(memberId: string) {
    try {
      const response = await apiClient.get(
        `${this.APIBaseUrl}/admin/organization/members/${memberId}`
      );
      return response.data;
    } catch (error) {
      console.error(
        "🚀 ~ TeamMemberService ~ getTeamMemberById ~ error:",
        error
      );
    }
  }

  async getTeamMemberByFilter(searchQuery: string): Promise<TeamMember[]> {
    try {
      let email = "";
      let name = "";

      if (searchQuery.includes("@")) {
        email = searchQuery;
      } else {
        name = searchQuery;
      }
      const response = await apiClient.get(
        `${this.APIBaseUrl}/admin/organization/members/search?email=${email}&name=${name}`
      );
      return response.data;
    } catch (error) {
      console.log(
        "🚀 ~ TeamMemberService ~ getTeamMemberByFilter ~ error:",
        error
      );
      return [];
    }
  }

  async inviteUserToOrg(email: string): Promise<any> {
    try {
      const payload = { email };
      const response = await apiClient.post(
        `${this.APIBaseUrl}/admin/users/invite`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error(
        "🚀 ~ TeamMemberService ~ inviteUserToOrganization ~ error:",
        error
      );
      throw error;
    }
  }

  async updateTeamMemberStatus(
    memberId: string,
    isActive: boolean
  ): Promise<void> {
    try {
      const response = await apiClient.patch(
        `${this.APIBaseUrl}/admin/organization/members/${memberId}`,
        {
          isActive,
        }
      );
      return response.data;
    } catch (error) {
      console.error(
        "🚀 ~ TeamMemberService ~ patchTeamMemberStatus ~ error:",
        error
      );
      throw error;
    }
  }

  async teamMemberSignUp(data: {
    firstName: string;
    lastName: string;
    password: string;
    token: string;
  }): Promise<any> {
    try {
      const response = await apiClient.post(
        `${this.APIBaseUrl}/auth/member/signup`,
        data,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      return response;
    } catch (error) {
      console.error(
        "🚀 ~ TeamMemberService ~ teamMemberSignUp ~ error:",
        error
      );
      throw error;
    }
  }

  async getAllInvites(page: number, limit: number) {
    try {
      const response = await apiClient.get(
        `${this.APIBaseUrl}/admin/users/invites/all?page=${page}&limit=${limit}`
      );
      const data = response.data;
      return data;
    } catch (error) {
      console.error("🚀 ~ TeamMemberService ~ getAllInvites ~ error:", error);
      return [];
    }
  }

  async getAllInvitesByStatus(status: string, page: number, limit: number) {
    try {
      const response = await apiClient.get(
        `${this.APIBaseUrl}/admin/users/invites/all?page=${page}&limit=${limit}&status=${status}`
      );
      const data = response.data;
      return data;
    } catch (error) {
      console.error(
        "🚀 ~ TeamMemberService ~ getAllInvitesByStatus ~ error:",
        error
      );
      return [];
    }
  }

  async getFilteredTeamMembers(
    searchQuery: string,
    page: number = 1,
    pageSize: number = 10
  ): Promise<{ members: TeamMember[]; total: number; totalPages: number }> {
    try {
      const searchParams = new URLSearchParams();
      searchParams.append("page", page.toString());
      searchParams.append("limit", pageSize.toString());

      if (searchQuery) {
        const datePattern = /^\d{2}-[A-Za-z]{3}-\d{2,4}$/;
        if (datePattern.test(searchQuery)) {
          const parts = searchQuery.split("-");
          if (parts[2].length === 2) {
            const year = parseInt(parts[2]);

            const fullYear = year < 50 ? 2000 + year : 1900 + year;
            searchQuery = `${parts[0]}-${parts[1]}-${fullYear}`;
          }
        }
        searchParams.append("search", searchQuery);
      }

      // Always use the filter endpoint for all searches
      const response = await apiClient.get(
        `${
          this.APIBaseUrl
        }/admin/organization/members/filter?${searchParams.toString()}`
      );

      if (response.data) {
        return {
          members: response.data.members || [],
          total: response.data.total || 0,
          totalPages:
            response.data.totalPages ||
            Math.ceil((response.data.total || 0) / pageSize),
        };
      }

      return { members: [], total: 0, totalPages: 0 };
    } catch (error) {
      console.error(
        "🚀 ~ TeamMemberService ~ getFilteredTeamMembers ~ error:",
        error
      );
      return { members: [], total: 0, totalPages: 0 };
    }
  }

  async resendInvite(inviteId: string): Promise<any> {
    try{
      const response = await apiClient.put(
        `${this.APIBaseUrl}/admin/users/invite/resend/${inviteId}`
      );
      return response.data;
    }catch(error:any){
      console.error("🚀 ~ TeamMemberService ~ resendInvite ~ error:", error);
    }
  }

  async deleteInvite(inviteId: string): Promise<any> {
    try{
      const response = await apiClient.delete(
        `${this.APIBaseUrl}/admin/users/invite/${inviteId}`
      );
      return response.data;
    }catch(error:any){
      console.error("🚀 ~ TeamMemberService ~ deleteInvite ~ error:", error);
    }
  }
}
const teamMemberService = new TeamMemberService();
export default teamMemberService;
